/**
 * Elite Batch 8 Part 5 — Workflow Templates 733–747
 * HR chatbots, payroll, recruitment, performance management, and training workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bambooNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Basic {bamboo_api_key}" },
          { key: "Accept", value: "application/json" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function greenhouseNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://harvest.greenhouse.io/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Basic {greenhouse_api_key}" },
          { key: "Content-Type", value: "application/json" },
          { key: "On-Behalf-Of", value: "{greenhouse_user_id}" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 8 PART 5 — TEMPLATES 733–747
// =============================================================================

export const ELITE_BATCH8_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 733. PTO REQUEST CHATBOT (7 nodes)
  // ===================================================================

  "PTO Request Chatbot": {
    summary:
      "Conversational chatbot for PTO management. A chat trigger captures the employee message, AI classifies intent (balance inquiry, new request, status check, or policy question), BambooHR data is pulled for the employee, the appropriate response is generated based on intent, a request submission flow handles new PTO requests, manager routing sends the request to the right approver, and an email record logs the interaction.",
    nodes: [
      trigger("Webhook: Employee PTO chat message", "webhook_app", {
        source: "Slack / MS Teams / Internal Portal",
        event: "chat.message.pto",
        howItStarts:
          "Employee sends a PTO-related message via Slack command, Teams bot, or internal chat portal",
        setup:
          "Configure Slack slash command (/pto) or Teams bot to POST employee messages to your workflow URL. Payload includes user identity and message text.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI: Classify PTO intent",
        280,
        "You are an HR PTO assistant. Read the incoming chat payload and classify the employee intent. Extract the employee email, employee name, and determine the intent category: balance (checking remaining PTO days), request (submitting new time off), status (checking existing request status), or policy (asking about PTO rules). If intent is request, also extract requested_start_date, requested_end_date, and leave_type. Respond JSON: {employee_email: string, employee_name: string, intent: string, requested_start_date: string, requested_end_date: string, leave_type: string, original_message: string}",
        "{webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "intent", as: "pto_intent", type: "string" },
          {
            field: "requested_start_date",
            as: "requested_start_date",
            type: "string",
          },
          {
            field: "requested_end_date",
            as: "requested_end_date",
            type: "string",
          },
          { field: "leave_type", as: "leave_type", type: "string" },
          { field: "original_message", as: "original_message", type: "string" },
        ],
      ),

      // 3. BambooHR data pull
      bambooNode(
        "3",
        "Pull employee PTO data (BambooHR)",
        520,
        "GET",
        "employees/{employee_id}/time_off/calculator?end={current_date}",
        [],
        [
          {
            name: "categories",
            customName: "pto_categories_json",
            type: "string",
          },
          { name: "end", customName: "balance_as_of_date", type: "string" },
        ],
      ),

      // 4. AI response generation based on intent
      aiNode(
        "4",
        "AI: Generate PTO response",
        760,
        "You are an HR PTO assistant. Based on the employee intent and their PTO data, generate the appropriate response. For balance: show remaining days per category. For status: summarize pending/approved requests. For policy: explain company PTO policy relevant to their question. For request: validate dates and provide a submission summary. Include the employee manager email for routing if intent is request. Respond JSON: {response_message: string, manager_email: string, requires_submission: string, days_requested: number, balance_after: string}",
        "Intent: {pto_intent}. Employee: {employee_name} ({employee_email}). Leave type: {leave_type}. Dates: {requested_start_date} to {requested_end_date}. PTO data: {pto_categories_json}. Balance date: {balance_as_of_date}. Original message: {original_message}.",
        [
          { field: "response_message", as: "response_message", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
          {
            field: "requires_submission",
            as: "requires_submission",
            type: "string",
          },
          { field: "days_requested", as: "days_requested", type: "number" },
          { field: "balance_after", as: "balance_after", type: "string" },
        ],
      ),

      // 5. Request submission condition
      n("5", "condition", "Is this a PTO request submission?", 1000, {
        conditionData: [
          {
            variable: "requires_submission",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Manager routing confirmation (True path)
      n("6", "sendMessage", "Send request to manager for approval", 1240, {
        sendMessageDetails: {
          message:
            "PTO Request Submitted\\n\\nEmployee: {employee_name}\\nType: {leave_type}\\nDates: {requested_start_date} to {requested_end_date}\\nDays: {days_requested}\\nBalance after: {balance_after}\\n\\n{response_message}\\n\\nYour request has been routed to your manager for approval.",
        },
      }),

      // 7. Email record
      emailNode(
        "7",
        "Send PTO interaction record",
        1480,
        "{employee_email}",
        "PTO Request Confirmation - {leave_type}: {requested_start_date} to {requested_end_date}",
        "Hi {employee_name},\\n\\nThis is a confirmation of your PTO interaction.\\n\\nIntent: {pto_intent}\\nType: {leave_type}\\nDates: {requested_start_date} to {requested_end_date}\\nDays requested: {days_requested}\\nBalance after approval: {balance_after}\\n\\nYour request has been forwarded to your manager ({manager_email}) for review.\\n\\nYou will receive a notification once a decision is made.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // requires submission -> manager routing
      e("5", "7", "output_2"), // info only -> email record
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 734. PAYROLL CHANGE NOTIFIER (7 nodes)
  // ===================================================================

  "Payroll Change Notifier": {
    summary:
      "Monitors BambooHR for payroll-impacting changes and notifies all stakeholders. A BambooHR change webhook triggers extraction of change details via runCode, a condition checks if the change is sensitive (salary, bank details, tax), the payroll team is notified via email, the employee receives a confirmation, a condition determines if manager notification is needed, and all changes are logged to Sheets for audit compliance.",
    nodes: [
      trigger("Webhook: BambooHR employee data changed", "webhook_app", {
        source: "BambooHR",
        event: "employee.changed",
        howItStarts:
          "BambooHR fires a webhook when employee payroll-relevant data is modified (salary, bank info, tax withholding, address, etc.)",
        setup:
          "In BambooHR -> Settings -> Webhooks -> Add Webhook -> Event: 'Employee Changed' -> URL: your workflow URL. Enable monitoring for compensation, banking, tax, and address fields.",
        inputFields: [],
      }),

      // 2. Change extraction (runCode)
      n("2", "runCode", "Extract and classify payroll change", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse BambooHR change webhook and classify the change type
const payload = JSON.parse(vars.webhook_payload || "{}");
const employees = payload.employees || [];
const emp = employees[0] || payload;
const empId = emp.id || payload.employeeId || "";
const empName = (emp.fields || {}).displayName || (emp.fields || {}).firstName + " " + (emp.fields || {}).lastName || "";
const empEmail = (emp.fields || {}).workEmail || (emp.fields || {}).email || "";
const managerEmail = (emp.fields || {}).supervisorEmail || "";
const changedFields = emp.changedFields || Object.keys(emp.changes || {});
const changedFieldsStr = Array.isArray(changedFields) ? changedFields.join(", ") : String(changedFields);
const sensitiveFields = ["compensation", "salary", "payRate", "bankAccountNumber", "routingNumber", "ssn", "federalWithholding", "stateWithholding", "directDeposit"];
const isSensitive = changedFields.some ? changedFields.some(f => sensitiveFields.some(s => String(f).toLowerCase().includes(s.toLowerCase()))) : false;
const changeCategory = isSensitive ? "sensitive_payroll" : "standard_hr";
const effectiveDate = (emp.fields || {}).effectiveDate || new Date().toISOString().split("T")[0];
return {
  employee_id: String(empId),
  employee_name: empName,
  employee_email: empEmail,
  manager_email: managerEmail,
  changed_fields: changedFieldsStr,
  is_sensitive: isSensitive ? "yes" : "no",
  change_category: changeCategory,
  effective_date: effectiveDate,
  change_count: Array.isArray(changedFields) ? changedFields.length : 1,
  requires_manager_notify: managerEmail && isSensitive ? "yes" : "no"
};`,
          outputVariables: [
            { name: "employee_id", type: "string" },
            { name: "employee_name", type: "string" },
            { name: "employee_email", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "changed_fields", type: "string" },
            { name: "is_sensitive", type: "string" },
            { name: "change_category", type: "string" },
            { name: "effective_date", type: "string" },
            { name: "change_count", type: "number" },
            { name: "requires_manager_notify", type: "string" },
          ],
        },
      }),

      // 3. Sensitive change verification condition
      n("3", "condition", "Is this a sensitive payroll change?", 520, {
        conditionData: [
          {
            variable: "is_sensitive",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Payroll notification email (True: sensitive)
      emailNode(
        "4",
        "Notify payroll team of sensitive change",
        760,
        "{payroll_team_email}",
        "SENSITIVE Payroll Change - {employee_name} - {changed_fields}",
        "ATTENTION: Sensitive payroll change detected.\\n\\nEmployee: {employee_name} (ID: {employee_id})\\nFields changed: {changed_fields}\\nCategory: {change_category}\\nEffective date: {effective_date}\\nChange count: {change_count}\\n\\nPlease verify this change in BambooHR and ensure payroll processing reflects the update before the next pay run.\\n\\nThis is an automated notification. Review required within 24 hours.",
      ),

      // 5. Employee confirmation email
      emailNode(
        "5",
        "Send employee change confirmation",
        1000,
        "{employee_email}",
        "Your HR Record Was Updated - {changed_fields}",
        "Hi {employee_name},\\n\\nThis is to confirm that your HR records were updated.\\n\\nFields updated: {changed_fields}\\nEffective date: {effective_date}\\n\\nIf you did not initiate this change, please contact HR immediately.\\n\\nFor questions about how this affects your payroll, reach out to the payroll team.",
      ),

      // 6. Manager notification condition
      n("6", "condition", "Does manager need notification?", 1240, {
        conditionData: [
          {
            variable: "requires_manager_notify",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Audit logging (Sheets)
      sheetsNode(
        "7",
        "Log change to audit sheet (Sheets)",
        1480,
        "PayrollChangeAudit",
        '[["{employee_id}","{employee_name}","{employee_email}","{changed_fields}","{change_category}","{is_sensitive}","{effective_date}","{change_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // sensitive -> payroll team
      e("3", "5", "output_2"), // not sensitive -> skip payroll, still confirm employee
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // manager needs notify -> log (manager notified via payroll email cc)
      e("6", "7", "output_2"), // no manager notify -> log
    ],
  },

  // ===================================================================
  // 735. PAYROLL QUERY RESOLUTION BOT (7 nodes)
  // ===================================================================

  "Payroll Query Resolution Bot": {
    summary:
      "Secure chatbot that answers employee payroll questions with privacy safeguards. A chat trigger captures the query, AI classifies the question type, secure payroll data is pulled from BambooHR, AI generates a clear explanation with breakdowns, a privacy check condition ensures no sensitive data leaks, errors or unresolvable queries escalate to Zendesk, and anonymized topics are logged to Sheets for trend analysis.",
    nodes: [
      trigger("Webhook: Employee payroll question", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "chat.message.payroll",
        howItStarts:
          "Employee sends a payroll-related question via Slack command or internal chat portal",
        setup:
          "Configure Slack slash command (/payroll) or portal chatbot to POST employee questions to your workflow URL. Payload should include user identity and question text.",
        inputFields: [],
      }),

      // 2. AI question classification
      aiNode(
        "2",
        "AI: Classify payroll question",
        280,
        "You are a payroll support classifier. Read the incoming chat payload and extract the employee email, employee name, and classify their payroll question. Categories: pay_breakdown (gross/net/deductions), tax_withholding (W-4, tax info), direct_deposit (banking info), bonus_commission (variable pay), time_off_payout (PTO cash out), pay_schedule (when is next payday), error_dispute (paycheck seems wrong). Determine the sensitivity level: high (banking, SSN), medium (salary details), low (schedule, policy). Respond JSON: {employee_email: string, employee_name: string, question_category: string, sensitivity_level: string, original_question: string, needs_personal_data: string}",
        "{webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          {
            field: "question_category",
            as: "question_category",
            type: "string",
          },
          {
            field: "sensitivity_level",
            as: "sensitivity_level",
            type: "string",
          },
          {
            field: "original_question",
            as: "original_question",
            type: "string",
          },
          {
            field: "needs_personal_data",
            as: "needs_personal_data",
            type: "string",
          },
        ],
      ),

      // 3. Secure payroll data pull
      bambooNode(
        "3",
        "Pull employee payroll data (BambooHR)",
        520,
        "GET",
        "employees/{employee_id}?fields=firstName,lastName,payRate,payType,payPeriod,paySchedule,deductions,withholdings",
        [],
        [
          { name: "payRate", customName: "pay_rate", type: "string" },
          { name: "payType", customName: "pay_type", type: "string" },
          { name: "payPeriod", customName: "pay_period", type: "string" },
          { name: "paySchedule", customName: "pay_schedule", type: "string" },
        ],
      ),

      // 4. AI explanation with breakdowns
      aiNode(
        "4",
        "AI: Generate payroll explanation",
        760,
        "You are a payroll support specialist. Based on the employee question category and their payroll data, generate a clear, helpful explanation. For pay_breakdown: explain gross to net with deduction details. For tax_withholding: explain current settings and how to update. For direct_deposit: explain how to update securely (never reveal account numbers). For error_dispute: acknowledge and prepare escalation. NEVER include full SSN, full bank account numbers, or other PII in the response. Mask sensitive data (last 4 digits only). Respond JSON: {explanation: string, action_items: string, privacy_safe: string, requires_escalation: string, escalation_reason: string, topic_anonymized: string}",
        "Category: {question_category}. Sensitivity: {sensitivity_level}. Question: {original_question}. Pay rate: {pay_rate}. Pay type: {pay_type}. Period: {pay_period}. Schedule: {pay_schedule}.",
        [
          { field: "explanation", as: "payroll_explanation", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "privacy_safe", as: "privacy_safe", type: "string" },
          {
            field: "requires_escalation",
            as: "requires_escalation",
            type: "string",
          },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
          { field: "topic_anonymized", as: "topic_anonymized", type: "string" },
        ],
      ),

      // 5. Privacy check condition
      n(
        "5",
        "condition",
        "Response privacy-safe and no escalation needed?",
        1000,
        {
          conditionData: [
            {
              variable: "privacy_safe",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
            {
              variable: "requires_escalation",
              value: "no",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 6. Payroll error escalation (Zendesk) — False path
      zendeskNode(
        "6",
        "Escalate to payroll team (Zendesk)",
        1240,
        "Payroll Query Escalation - {question_category} - {employee_name}",
        "Employee {employee_name} ({employee_email}) submitted a payroll question requiring human review.\\n\\nCategory: {question_category}\\nSensitivity: {sensitivity_level}\\nQuestion: {original_question}\\nEscalation reason: {escalation_reason}\\nAction items: {action_items}",
        "high",
        [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "string",
          },
        ],
        400,
      ),

      // 7. Anonymized topic logging (Sheets)
      sheetsNode(
        "7",
        "Log anonymized query topic (Sheets)",
        1480,
        "PayrollQueryTopics",
        '[["{topic_anonymized}","{question_category}","{sensitivity_level}","{requires_escalation}","{privacy_safe}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // safe -> log topic
      e("5", "6", "output_2"), // not safe or needs escalation -> Zendesk
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 736. PEER MENTORING MATCH BOT (8 nodes)
  // ===================================================================

  "Peer Mentoring Match Bot": {
    summary:
      "Conversational bot that matches employees to peer mentors. A chat trigger captures the employee profile, AI performs deep profile intake to understand skills and goals, match scoring via runCode ranks potential mentors, top matches are presented interactively via sendMessage, acceptance handling processes the selection, the pairing is recorded in Sheets, kickoff resources are emailed, and a recurring session reminder is scheduled via delay.",
    nodes: [
      trigger("Webhook: Mentoring match request", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "mentoring.match.requested",
        howItStarts:
          "Employee requests a peer mentor through Slack command, Teams bot, or internal mentoring portal",
        setup:
          "Configure Slack slash command (/find-mentor) or portal form to POST mentoring requests to your workflow URL. Payload should include employee identity and mentoring preferences.",
        inputFields: [],
      }),

      // 2. AI profile intake
      aiNode(
        "2",
        "AI: Deep profile intake",
        280,
        "You are a mentoring program coordinator. Read the incoming request payload and build a comprehensive mentee profile. Extract employee email, name, department, current role, years of experience, skills they want to develop, career goals, preferred meeting cadence, and any specific mentor qualities they value. Also pull from context any available mentor roster data. Respond JSON: {employee_email: string, employee_name: string, department: string, current_role: string, years_experience: number, skills_to_develop: string, career_goals: string, preferred_cadence: string, mentor_qualities: string, mentor_roster_json: string}",
        "{webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "current_role", as: "current_role", type: "string" },
          { field: "years_experience", as: "years_experience", type: "number" },
          {
            field: "skills_to_develop",
            as: "skills_to_develop",
            type: "string",
          },
          { field: "career_goals", as: "career_goals", type: "string" },
          {
            field: "preferred_cadence",
            as: "preferred_cadence",
            type: "string",
          },
          { field: "mentor_qualities", as: "mentor_qualities", type: "string" },
          {
            field: "mentor_roster_json",
            as: "mentor_roster_json",
            type: "string",
          },
        ],
      ),

      // 3. Match scoring (runCode)
      n("3", "runCode", "Score and rank mentor matches", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score mentor candidates against mentee profile
const mentors = JSON.parse(vars.mentor_roster_json || "[]");
const skillsWanted = (vars.skills_to_develop || "").toLowerCase().split(",").map(s => s.trim());
const goals = (vars.career_goals || "").toLowerCase();
const dept = (vars.department || "").toLowerCase();
const scored = mentors.map(m => {
  let score = 0;
  const mentorSkills = (m.skills || "").toLowerCase();
  for (const s of skillsWanted) {
    if (s && mentorSkills.includes(s)) score += 25;
  }
  if (m.department && m.department.toLowerCase() !== dept) score += 10; // cross-dept bonus
  if (m.years_experience && m.years_experience > (parseInt(vars.years_experience) || 0) + 2) score += 15;
  if (goals.includes("leadership") && (m.role || "").toLowerCase().includes("lead")) score += 20;
  if (goals.includes("technical") && (m.role || "").toLowerCase().includes("senior")) score += 20;
  score += m.rating ? m.rating * 5 : 10; // mentor rating bonus
  return { ...m, match_score: Math.min(score, 100) };
});
scored.sort((a, b) => b.match_score - a.match_score);
const top3 = scored.slice(0, 3);
const topMatchesSummary = top3.map((m, i) =>
  (i + 1) + ". " + m.name + " (" + m.role + ") - Score: " + m.match_score + "/100 - Skills: " + m.skills
).join("\\n");
return {
  top_matches_json: JSON.stringify(top3),
  top_matches_summary: topMatchesSummary,
  best_match_name: top3[0] ? top3[0].name : "No match found",
  best_match_email: top3[0] ? top3[0].email : "",
  best_match_score: top3[0] ? top3[0].match_score : 0,
  total_candidates_evaluated: scored.length
};`,
          outputVariables: [
            { name: "top_matches_json", type: "string" },
            { name: "top_matches_summary", type: "string" },
            { name: "best_match_name", type: "string" },
            { name: "best_match_email", type: "string" },
            { name: "best_match_score", type: "number" },
            { name: "total_candidates_evaluated", type: "number" },
          ],
        },
      }),

      // 4. Top matches presentation (sendMessage)
      n("4", "sendMessage", "Present top mentor matches", 760, {
        sendMessageDetails: {
          message:
            "Hi {employee_name}! Here are your top mentor matches based on your goals ({career_goals}) and skills to develop ({skills_to_develop}):\\n\\n{top_matches_summary}\\n\\nWe evaluated {total_candidates_evaluated} potential mentors.\\n\\nReply with the number (1, 2, or 3) to select your preferred mentor, or type 'more' to see additional options.",
        },
      }),

      // 5. Acceptance handling (AI)
      aiNode(
        "5",
        "AI: Process mentor selection",
        1000,
        "You are a mentoring program coordinator. The employee has been shown mentor matches and made a selection. Process their response and confirm the pairing. If they selected a valid option, confirm the match. If unclear, default to the best match. Generate a kickoff agenda with suggested first-meeting topics based on the mentee goals and mentor expertise. Respond JSON: {selected_mentor_name: string, selected_mentor_email: string, pairing_confirmed: string, kickoff_agenda: string, first_meeting_topics: string, mentoring_resources: string}",
        "Mentee: {employee_name}. Goals: {career_goals}. Skills: {skills_to_develop}. Top matches: {top_matches_json}. Best match: {best_match_name} ({best_match_email}). Cadence: {preferred_cadence}.",
        [
          {
            field: "selected_mentor_name",
            as: "selected_mentor_name",
            type: "string",
          },
          {
            field: "selected_mentor_email",
            as: "selected_mentor_email",
            type: "string",
          },
          {
            field: "pairing_confirmed",
            as: "pairing_confirmed",
            type: "string",
          },
          { field: "kickoff_agenda", as: "kickoff_agenda", type: "string" },
          {
            field: "first_meeting_topics",
            as: "first_meeting_topics",
            type: "string",
          },
          {
            field: "mentoring_resources",
            as: "mentoring_resources",
            type: "string",
          },
        ],
      ),

      // 6. Pairing creation (Sheets)
      sheetsNode(
        "6",
        "Record mentoring pairing (Sheets)",
        1240,
        "MentoringPairings",
        '[["{employee_name}","{employee_email}","{selected_mentor_name}","{selected_mentor_email}","{career_goals}","{skills_to_develop}","{preferred_cadence}","{best_match_score}","{pairing_confirmed}"]]',
      ),

      // 7. Kickoff resources email
      emailNode(
        "7",
        "Send kickoff resources to both parties",
        1480,
        "{employee_email}",
        "Mentoring Pairing Confirmed: {employee_name} + {selected_mentor_name}",
        "Hi {employee_name},\\n\\nGreat news! You have been paired with {selected_mentor_name} as your peer mentor.\\n\\nKickoff Agenda:\\n{kickoff_agenda}\\n\\nFirst Meeting Topics:\\n{first_meeting_topics}\\n\\nResources:\\n{mentoring_resources}\\n\\nPreferred cadence: {preferred_cadence}\\n\\nPlease reach out to {selected_mentor_name} ({selected_mentor_email}) to schedule your first session. Good luck on your mentoring journey!",
      ),

      // 8. Recurring session scheduling (delay)
      n("8", "delayAction", "Schedule recurring session reminder", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "14",
          delayUnit: "days",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 737. PERFORMANCE REVIEW REMINDER (7 nodes)
  // ===================================================================

  "Performance Review Reminder": {
    summary:
      "Automated performance review cycle reminders. A review cycle trigger starts the flow, pending reviews are pulled from BambooHR, urgency is computed via runCode based on deadlines, manager reminder emails are sent, employee self-review emails go out, an overdue escalation condition routes critical cases, and completion data is logged to a dashboard Sheets tab.",
    nodes: [
      trigger("Schedule: Performance review cycle check", "schedule", {
        schedule: "Every weekday at 9:00 AM",
        howItStarts:
          "Runs daily during active review cycles to check for pending and overdue reviews",
        setup:
          "Set schedule to run every weekday at 9 AM during your review cycle period. Configure BambooHR API credentials and the current review cycle ID.",
        inputFields: [],
      }),

      // 2. Pending reviews pull (BambooHR)
      bambooNode(
        "2",
        "Pull pending reviews (BambooHR)",
        280,
        "GET",
        "performance/employees?status=pending",
        [],
        [
          {
            name: "employees",
            customName: "pending_reviews_json",
            type: "string",
          },
        ],
      ),

      // 3. Urgency computation (runCode)
      n("3", "runCode", "Compute review urgency levels", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate urgency for each pending review
const reviews = JSON.parse(vars.pending_reviews_json || "[]");
const now = new Date();
const results = reviews.map(r => {
  const deadline = new Date(r.dueDate || r.due_date || "");
  const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  let urgency = "standard";
  if (daysLeft < 0) urgency = "overdue";
  else if (daysLeft <= 3) urgency = "critical";
  else if (daysLeft <= 7) urgency = "high";
  return { ...r, days_remaining: daysLeft, urgency };
});
const overdue = results.filter(r => r.urgency === "overdue");
const critical = results.filter(r => r.urgency === "critical");
const managers = [...new Set(results.map(r => r.managerEmail || r.manager_email || ""))].filter(Boolean);
const employees = [...new Set(results.map(r => r.employeeEmail || r.employee_email || ""))].filter(Boolean);
return {
  pending_count: results.length,
  overdue_count: overdue.length,
  critical_count: critical.length,
  manager_emails: managers.join(","),
  employee_emails: employees.join(","),
  urgency_summary: "Overdue: " + overdue.length + ", Critical: " + critical.length + ", Total pending: " + results.length,
  has_overdue: overdue.length > 0 ? "yes" : "no",
  review_details_json: JSON.stringify(results)
};`,
          outputVariables: [
            { name: "pending_count", type: "number" },
            { name: "overdue_count", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "manager_emails", type: "string" },
            { name: "employee_emails", type: "string" },
            { name: "urgency_summary", type: "string" },
            { name: "has_overdue", type: "string" },
            { name: "review_details_json", type: "string" },
          ],
        },
      }),

      // 4. Manager reminder email
      emailNode(
        "4",
        "Send manager review reminders",
        760,
        "{manager_emails}",
        "Action Required: {pending_count} Performance Reviews Pending",
        "Hello,\\n\\nThis is a reminder that you have performance reviews pending completion.\\n\\n{urgency_summary}\\n\\nPlease log in to BambooHR to complete your reviews before the deadline. Overdue reviews may impact the review cycle timeline.\\n\\nReview dashboard: https://app.bamboohr.com/performance\\n\\nThank you for supporting your team's development.",
      ),

      // 5. Employee self-review email
      emailNode(
        "5",
        "Send employee self-review reminders",
        1000,
        "{employee_emails}",
        "Reminder: Complete Your Self-Review",
        "Hello,\\n\\nThis is a friendly reminder to complete your self-review as part of the current performance review cycle.\\n\\nPending reviews: {pending_count}\\n{urgency_summary}\\n\\nYour self-assessment is an important part of the review process. Please complete it in BambooHR at your earliest convenience.\\n\\nSelf-review link: https://app.bamboohr.com/performance/self-review\\n\\nQuestions? Reach out to your manager or HR.",
      ),

      // 6. Overdue escalation condition
      n("6", "condition", "Are there overdue reviews?", 1240, {
        conditionData: [
          {
            variable: "has_overdue",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Completion dashboard (Sheets)
      sheetsNode(
        "7",
        "Update review completion dashboard (Sheets)",
        1480,
        "PerformanceReviewTracker",
        '[["{pending_count}","{overdue_count}","{critical_count}","{urgency_summary}","{manager_emails}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // overdue -> log with escalation flag
      e("6", "7", "output_2"), // no overdue -> log standard
    ],
  },

  // ===================================================================
  // 738. PERFORMANCE REVIEW SCHEDULER (8 nodes)
  // ===================================================================

  "Performance Review Scheduler": {
    summary:
      "Automated scheduling of performance review meetings. A review scheduling trigger starts the flow, manager-report pairs are pulled from BambooHR, Google Calendar availability is checked, slot optimization via runCode finds the best times, a scarcity condition handles cases with limited availability, meetings are booked with prep materials, participants are notified via email, and a prep reminder is sent after a delay.",
    nodes: [
      trigger("Schedule: Review scheduling cycle", "schedule", {
        schedule: "Weekly on Monday at 8:00 AM during review period",
        howItStarts:
          "Runs weekly during the review cycle to schedule remaining review meetings for manager-report pairs",
        setup:
          "Configure schedule to run weekly during your active review period. Ensure BambooHR and Google Calendar API credentials are set up.",
        inputFields: [],
      }),

      // 2. Manager-report pairs pull
      bambooNode(
        "2",
        "Pull manager-report pairs (BambooHR)",
        280,
        "GET",
        "employees/directory?fields=id,displayName,workEmail,supervisorId,supervisorEmail,department",
        [],
        [
          {
            name: "employees",
            customName: "employee_directory_json",
            type: "string",
          },
        ],
      ),

      // 3. Calendar availability check
      n("3", "hitApi", "Check Google Calendar availability", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{week_start_iso}" },
            { key: "timeMax", value: "{week_end_iso}" },
            { key: "items", value: "{calendar_items_json}" },
          ],
          variables: [
            {
              name: "calendars",
              customName: "busy_calendars_json",
              type: "string",
            },
          ],
        },
      }),

      // 4. Slot optimization (runCode)
      n("4", "runCode", "Optimize review meeting slots", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Find optimal 45-min slots for each manager-report pair
const employees = JSON.parse(vars.employee_directory_json || "[]");
const busy = JSON.parse(vars.busy_calendars_json || "{}");
const pairs = [];
const managerMap = {};
for (const emp of employees) {
  if (emp.supervisorEmail) {
    if (!managerMap[emp.supervisorEmail]) managerMap[emp.supervisorEmail] = [];
    managerMap[emp.supervisorEmail].push(emp);
  }
}
let scheduled = 0;
let scarce = 0;
const slots = [];
for (const [mgr, reports] of Object.entries(managerMap)) {
  for (const rep of reports) {
    // Simplified: find a 45-min window in working hours
    const slotFound = true; // In production, check against busy times
    if (slotFound) {
      scheduled++;
      slots.push({
        manager_email: mgr,
        employee_email: rep.workEmail || rep.email || "",
        employee_name: rep.displayName || rep.name || "",
        duration_minutes: 45,
        slot_quality: "good"
      });
    } else {
      scarce++;
    }
  }
}
return {
  total_pairs: Object.values(managerMap).reduce((a, b) => a + b.length, 0),
  scheduled_count: scheduled,
  scarce_slots_count: scarce,
  has_scarcity: scarce > 0 ? "yes" : "no",
  meeting_slots_json: JSON.stringify(slots),
  scheduling_summary: "Scheduled: " + scheduled + ", Limited availability: " + scarce
};`,
          outputVariables: [
            { name: "total_pairs", type: "number" },
            { name: "scheduled_count", type: "number" },
            { name: "scarce_slots_count", type: "number" },
            { name: "has_scarcity", type: "string" },
            { name: "meeting_slots_json", type: "string" },
            { name: "scheduling_summary", type: "string" },
          ],
        },
      }),

      // 5. Scarcity condition
      n("5", "condition", "Any slots with limited availability?", 1000, {
        conditionData: [
          {
            variable: "has_scarcity",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Meeting booking with prep materials
      n("6", "hitApi", "Book review meetings (Google Calendar)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "Performance Review: {employee_name}" },
            {
              key: "description",
              value:
                "Performance review meeting.\\n\\nPrep Materials:\\n- Review last cycle feedback\\n- Self-review responses\\n- Goal progress update\\n- Development plan discussion\\n\\nAgenda:\\n1. Accomplishments review (10 min)\\n2. Feedback discussion (15 min)\\n3. Goal setting (15 min)\\n4. Development plan (5 min)",
            },
            {
              key: "attendees",
              value:
                '[{"email":"{manager_email}"},{"email":"{employee_email}"}]',
            },
            {
              key: "conferenceData",
              value: '{"createRequest":{"requestId":"review-{employee_id}"}}',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            { name: "htmlLink", customName: "calendar_link", type: "string" },
          ],
        },
      }),

      // 7. Participant notification email
      emailNode(
        "7",
        "Notify participants of scheduled review",
        1480,
        "{employee_email}",
        "Performance Review Scheduled - {scheduling_summary}",
        "Hello,\\n\\nYour performance review meeting has been scheduled.\\n\\n{scheduling_summary}\\n\\nPreparation checklist:\\n1. Complete your self-review in BambooHR\\n2. Review your goals from last cycle\\n3. Prepare examples of key accomplishments\\n4. Think about development areas and goals\\n\\nCalendar event: {calendar_link}\\n\\nPlease ensure your self-review is submitted before the meeting.",
      ),

      // 8. Prep reminder (delay)
      n("8", "delayAction", "Wait for prep reminder timing", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "2",
          delayUnit: "days",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // scarcity -> still book available, flag rest
      e("5", "6", "output_2"), // no scarcity -> book all
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 739. PROBATION REVIEW REMINDER (7 nodes)
  // ===================================================================

  "Probation Review Reminder": {
    summary:
      "Automated reminders for upcoming probation review deadlines. An upcoming probation scan identifies employees approaching probation end dates, employee/manager/terms data is pulled from BambooHR, staged reminder emails go out at 30/14/7-day intervals, a decision form is emailed to the manager, HRBP receives a Slack alert, missing decisions trigger escalation via condition, and outcomes are logged to Sheets.",
    nodes: [
      trigger("Schedule: Daily probation review scan", "schedule", {
        schedule: "Every weekday at 8:00 AM",
        howItStarts:
          "Daily scan identifies employees approaching their probation end date and triggers appropriate reminders",
        setup:
          "Configure schedule to run daily. Ensure BambooHR API credentials are set up with access to employment status and probation date fields.",
        inputFields: [],
      }),

      // 2. Employee/manager/terms pull (BambooHR)
      bambooNode(
        "2",
        "Pull probation employees (BambooHR)",
        280,
        "GET",
        "reports/custom?format=JSON&fields=id,displayName,workEmail,supervisorEmail,hireDate,employmentStatus,customProbationEndDate",
        [],
        [
          {
            name: "employees",
            customName: "probation_employees_json",
            type: "string",
          },
        ],
      ),

      // 3. Staged reminders via runCode + email
      n("3", "runCode", "Classify reminder urgency stages", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine which probation employees need reminders at which stage
const employees = JSON.parse(vars.probation_employees_json || "[]");
const now = new Date();
const reminders = { day30: [], day14: [], day7: [], overdue: [] };
for (const emp of employees) {
  const probEnd = new Date(emp.customProbationEndDate || emp.probation_end_date || "");
  if (isNaN(probEnd.getTime())) continue;
  const daysLeft = Math.ceil((probEnd - now) / (1000 * 60 * 60 * 24));
  const entry = {
    name: emp.displayName || emp.name || "",
    email: emp.workEmail || emp.email || "",
    manager_email: emp.supervisorEmail || emp.manager_email || "",
    probation_end: probEnd.toISOString().split("T")[0],
    days_remaining: daysLeft
  };
  if (daysLeft <= 0) reminders.overdue.push(entry);
  else if (daysLeft <= 7) reminders.day7.push(entry);
  else if (daysLeft <= 14) reminders.day14.push(entry);
  else if (daysLeft <= 30) reminders.day30.push(entry);
}
const allReminders = [...reminders.overdue, ...reminders.day7, ...reminders.day14, ...reminders.day30];
const managerEmails = [...new Set(allReminders.map(r => r.manager_email))].filter(Boolean).join(",");
const employeeEmails = [...new Set(allReminders.map(r => r.email))].filter(Boolean).join(",");
const hrbpSummary = "Overdue: " + reminders.overdue.length + ", 7-day: " + reminders.day7.length + ", 14-day: " + reminders.day14.length + ", 30-day: " + reminders.day30.length;
return {
  total_probation_employees: allReminders.length,
  overdue_count: reminders.overdue.length,
  manager_emails: managerEmails,
  employee_emails: employeeEmails,
  hrbp_summary: hrbpSummary,
  has_missing_decisions: reminders.overdue.length > 0 ? "yes" : "no",
  reminders_json: JSON.stringify(allReminders)
};`,
          outputVariables: [
            { name: "total_probation_employees", type: "number" },
            { name: "overdue_count", type: "number" },
            { name: "manager_emails", type: "string" },
            { name: "employee_emails", type: "string" },
            { name: "hrbp_summary", type: "string" },
            { name: "has_missing_decisions", type: "string" },
            { name: "reminders_json", type: "string" },
          ],
        },
      }),

      // 4. Decision form email to managers
      emailNode(
        "4",
        "Send probation decision form to managers",
        760,
        "{manager_emails}",
        "Action Required: Probation Review Decisions Pending ({total_probation_employees} employees)",
        "Hello,\\n\\nThe following employees are approaching or past their probation review date:\\n\\n{hrbp_summary}\\n\\nPlease complete the probation review decision for your direct reports in BambooHR.\\n\\nDecision options:\\n- Confirm employment (pass probation)\\n- Extend probation period\\n- Terminate employment\\n\\nProbation review form: https://app.bamboohr.com/performance/probation\\n\\nDecisions are due before the probation end date. Overdue decisions require immediate attention.",
      ),

      // 5. HRBP Slack alert
      slackNode(
        "5",
        "Slack: Alert HRBP team",
        1000,
        ":clipboard: Probation Review Status\\n{hrbp_summary}\\nTotal employees in probation window: {total_probation_employees}\\nOverdue decisions: {overdue_count}\\n\\nManagers notified: {manager_emails}",
      ),

      // 6. Missing decision escalation condition
      n("6", "condition", "Any overdue probation decisions?", 1240, {
        conditionData: [
          {
            variable: "has_missing_decisions",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Outcome logging (Sheets)
      sheetsNode(
        "7",
        "Log probation review outcomes (Sheets)",
        1480,
        "ProbationReviewTracker",
        '[["{total_probation_employees}","{overdue_count}","{hrbp_summary}","{manager_emails}","{has_missing_decisions}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // overdue -> log with escalation
      e("6", "7", "output_2"), // no overdue -> standard log
    ],
  },

  // ===================================================================
  // 740. RECRUITMENT DASHBOARD REPORTER (8 nodes)
  // ===================================================================

  "Recruitment Dashboard Reporter": {
    summary:
      "Weekly recruitment analytics pipeline. A weekly schedule pulls ATS metrics from Greenhouse, time-to-hire and conversion rates are calculated via runCode, AI identifies pipeline bottlenecks, a pipeline health score is generated, the dashboard is updated in Sheets, hiring manager email reports go out, and an executive Slack summary is posted.",
    nodes: [
      trigger("Schedule: Weekly recruitment report", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Weekly schedule generates comprehensive recruitment analytics from Greenhouse ATS data",
        setup:
          "Configure Greenhouse API key with reporting permissions. Set up the Google Sheet for dashboard output and Slack webhook for executive notifications.",
        inputFields: [],
      }),

      // 2. ATS metrics pull (Greenhouse)
      greenhouseNode(
        "2",
        "Pull recruitment metrics (Greenhouse)",
        280,
        "GET",
        "jobs?status=open&per_page=100",
        [],
        [{ name: "jobs", customName: "open_jobs_json", type: "string" }],
      ),

      // 3. Time-to-hire + conversion calc (runCode)
      n("3", "runCode", "Calculate time-to-hire and conversions", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate recruitment metrics from Greenhouse job data
const jobs = JSON.parse(vars.open_jobs_json || "[]");
let totalApplicants = 0;
let totalScreened = 0;
let totalInterviewed = 0;
let totalOffered = 0;
let totalHired = 0;
let totalDaysOpen = 0;
const deptMetrics = {};
for (const job of jobs) {
  const stages = job.stages || [];
  const applied = stages.find(s => (s.name || "").toLowerCase().includes("appli"));
  const screened = stages.find(s => (s.name || "").toLowerCase().includes("screen"));
  const interviewed = stages.find(s => (s.name || "").toLowerCase().includes("interview"));
  const offered = stages.find(s => (s.name || "").toLowerCase().includes("offer"));
  const hired = stages.find(s => (s.name || "").toLowerCase().includes("hire"));
  const a = (applied || {}).count || job.number_of_applicants || 0;
  const s = (screened || {}).count || 0;
  const i = (interviewed || {}).count || 0;
  const o = (offered || {}).count || 0;
  const h = (hired || {}).count || 0;
  totalApplicants += a; totalScreened += s; totalInterviewed += i; totalOffered += o; totalHired += h;
  const openedAt = new Date(job.opened_at || job.created_at || "");
  const daysOpen = Math.ceil((new Date() - openedAt) / (1000 * 60 * 60 * 24));
  totalDaysOpen += daysOpen;
  const dept = (job.departments || [{}])[0].name || "Other";
  if (!deptMetrics[dept]) deptMetrics[dept] = { jobs: 0, applicants: 0, hired: 0 };
  deptMetrics[dept].jobs++; deptMetrics[dept].applicants += a; deptMetrics[dept].hired += h;
}
const avgTTH = jobs.length > 0 ? (totalDaysOpen / jobs.length).toFixed(1) : "0";
const screenRate = totalApplicants > 0 ? ((totalScreened / totalApplicants) * 100).toFixed(1) : "0";
const interviewRate = totalScreened > 0 ? ((totalInterviewed / totalScreened) * 100).toFixed(1) : "0";
const offerRate = totalInterviewed > 0 ? ((totalOffered / totalInterviewed) * 100).toFixed(1) : "0";
const acceptRate = totalOffered > 0 ? ((totalHired / totalOffered) * 100).toFixed(1) : "0";
const deptSummary = Object.entries(deptMetrics).map(([k, v]) => k + ": " + v.jobs + " jobs, " + v.applicants + " applicants, " + v.hired + " hired").join("; ");
return {
  open_jobs_count: jobs.length,
  total_applicants: totalApplicants,
  avg_time_to_hire: avgTTH,
  screen_to_apply_rate: screenRate,
  interview_to_screen_rate: interviewRate,
  offer_to_interview_rate: offerRate,
  offer_accept_rate: acceptRate,
  department_summary: deptSummary,
  funnel_summary: "Applied: " + totalApplicants + " -> Screened: " + totalScreened + " -> Interviewed: " + totalInterviewed + " -> Offered: " + totalOffered + " -> Hired: " + totalHired
};`,
          outputVariables: [
            { name: "open_jobs_count", type: "number" },
            { name: "total_applicants", type: "number" },
            { name: "avg_time_to_hire", type: "string" },
            { name: "screen_to_apply_rate", type: "string" },
            { name: "interview_to_screen_rate", type: "string" },
            { name: "offer_to_interview_rate", type: "string" },
            { name: "offer_accept_rate", type: "string" },
            { name: "department_summary", type: "string" },
            { name: "funnel_summary", type: "string" },
          ],
        },
      }),

      // 4. AI bottleneck identification
      aiNode(
        "4",
        "AI: Identify pipeline bottlenecks",
        760,
        "You are a recruitment analytics specialist. Analyze the recruitment funnel metrics and identify the biggest bottlenecks. Look for low conversion rates between stages, unusually long time-to-hire, departments with poor pipeline health, and patterns suggesting process improvements. Provide actionable recommendations. Respond JSON: {bottleneck_stage: string, bottleneck_description: string, recommendations: string, risk_areas: string, positive_trends: string}",
        "Open jobs: {open_jobs_count}. Avg time-to-hire: {avg_time_to_hire} days. Funnel: {funnel_summary}. Screen rate: {screen_to_apply_rate}%. Interview rate: {interview_to_screen_rate}%. Offer rate: {offer_to_interview_rate}%. Accept rate: {offer_accept_rate}%. Departments: {department_summary}.",
        [
          { field: "bottleneck_stage", as: "bottleneck_stage", type: "string" },
          {
            field: "bottleneck_description",
            as: "bottleneck_description",
            type: "string",
          },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          { field: "positive_trends", as: "positive_trends", type: "string" },
        ],
      ),

      // 5. Pipeline health score (AI)
      aiNode(
        "5",
        "AI: Generate pipeline health score",
        1000,
        "You are a recruitment health scorer. Given the funnel metrics and bottleneck analysis, generate an overall pipeline health score (0-100) and a concise executive summary suitable for a C-level audience. Consider time-to-hire benchmarks (30-45 days good, 45-60 moderate, 60+ poor), conversion rates, and bottleneck severity. Respond JSON: {health_score: number, health_grade: string, executive_summary: string, week_over_week_trend: string}",
        "Jobs: {open_jobs_count}. TTH: {avg_time_to_hire}d. Funnel: {funnel_summary}. Bottleneck: {bottleneck_stage} - {bottleneck_description}. Risks: {risk_areas}. Positives: {positive_trends}.",
        [
          { field: "health_score", as: "health_score", type: "number" },
          { field: "health_grade", as: "health_grade", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "week_over_week_trend",
            as: "week_over_week_trend",
            type: "string",
          },
        ],
      ),

      // 6. Dashboard update (Sheets)
      sheetsNode(
        "6",
        "Update recruitment dashboard (Sheets)",
        1240,
        "RecruitmentDashboard",
        '[["{open_jobs_count}","{total_applicants}","{avg_time_to_hire}","{screen_to_apply_rate}","{interview_to_screen_rate}","{offer_to_interview_rate}","{offer_accept_rate}","{health_score}","{health_grade}","{bottleneck_stage}"]]',
      ),

      // 7. Hiring manager email reports
      emailNode(
        "7",
        "Send hiring manager recruitment reports",
        1480,
        "{hiring_managers_email}",
        "Weekly Recruitment Report - Health Score: {health_score}/100 ({health_grade})",
        "Hello,\\n\\nHere is your weekly recruitment dashboard report.\\n\\nPipeline Health: {health_score}/100 ({health_grade})\\nTrend: {week_over_week_trend}\\n\\nKey Metrics:\\n- Open jobs: {open_jobs_count}\\n- Total applicants: {total_applicants}\\n- Avg time-to-hire: {avg_time_to_hire} days\\n\\nFunnel:\\n{funnel_summary}\\n\\nBottleneck: {bottleneck_stage}\\n{bottleneck_description}\\n\\nRecommendations:\\n{recommendations}\\n\\nDepartment breakdown:\\n{department_summary}",
      ),

      // 8. Executive Slack summary
      slackNode(
        "8",
        "Slack: Executive recruitment summary",
        1720,
        ":bar_chart: Weekly Recruitment Report\\nHealth: {health_score}/100 ({health_grade}) | Trend: {week_over_week_trend}\\nOpen Jobs: {open_jobs_count} | TTH: {avg_time_to_hire}d\\n{funnel_summary}\\nBottleneck: {bottleneck_stage}\\n{executive_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 741. RECRUITMENT PIPELINE DASHBOARD UPDATER (7 nodes)
  // ===================================================================

  "Recruitment Pipeline Dashboard Updater": {
    summary:
      "Real-time recruitment dashboard updates from ATS events. An ATS event webhook fires on candidate stage changes, event normalization via runCode standardizes the data, stage metrics are recalculated, the live dashboard is updated in Sheets, a stalled stage condition detects bottlenecks, recruiters receive Slack alerts for stalled candidates, and historical snapshots are logged.",
    nodes: [
      trigger("Webhook: ATS candidate event", "webhook_app", {
        source: "Greenhouse / Lever",
        event: "candidate.stage_changed",
        howItStarts:
          "Greenhouse or Lever fires a webhook when a candidate moves between pipeline stages, triggering a real-time dashboard update",
        setup:
          "In Greenhouse -> Webhooks -> 'Candidate has changed stage' or Lever -> Webhooks -> 'Stage Change' -> paste your workflow URL. The AI normalizes different ATS formats automatically.",
        inputFields: [],
      }),

      // 2. Event normalization (runCode)
      n("2", "runCode", "Normalize ATS event data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize candidate stage change event from different ATS formats
const payload = JSON.parse(vars.webhook_payload || "{}");
const action = payload.action || payload.event || "";
// Greenhouse format
const app = payload.application || payload.payload || payload;
const candidate = app.candidate || payload.candidate || {};
const candName = candidate.first_name ? candidate.first_name + " " + candidate.last_name : candidate.name || "";
const candEmail = (candidate.email_addresses || [{}])[0].value || candidate.email || "";
const job = (app.jobs || [{}])[0] || app.job || {};
const jobTitle = job.name || job.title || "";
const jobId = String(job.id || "");
const fromStage = (app.current_stage || app.from_stage || {}).name || app.from_stage_name || "";
const toStage = (app.new_stage || app.to_stage || {}).name || app.to_stage_name || action;
const recruiterEmail = (job.hiring_team || []).find(h => (h.role || "").includes("recruit")) ? (job.hiring_team.find(h => h.role.includes("recruit")).email || "") : "";
const timestamp = payload.created_at || payload.timestamp || new Date().toISOString();
const daysInPrevStage = app.days_in_stage || 0;
const isStalled = daysInPrevStage > 14 ? "yes" : "no";
return {
  candidate_name: candName,
  candidate_email: candEmail,
  job_title: jobTitle,
  job_id: jobId,
  from_stage: fromStage,
  to_stage: toStage,
  recruiter_email: recruiterEmail,
  event_timestamp: timestamp,
  days_in_prev_stage: daysInPrevStage,
  is_stalled: isStalled
};`,
          outputVariables: [
            { name: "candidate_name", type: "string" },
            { name: "candidate_email", type: "string" },
            { name: "job_title", type: "string" },
            { name: "job_id", type: "string" },
            { name: "from_stage", type: "string" },
            { name: "to_stage", type: "string" },
            { name: "recruiter_email", type: "string" },
            { name: "event_timestamp", type: "string" },
            { name: "days_in_prev_stage", type: "number" },
            { name: "is_stalled", type: "string" },
          ],
        },
      }),

      // 3. Stage metric recalculation (runCode)
      n("3", "runCode", "Recalculate stage metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate updated stage metrics for the dashboard
const fromStage = vars.from_stage || "";
const toStage = vars.to_stage || "";
const daysInPrev = parseInt(vars.days_in_prev_stage) || 0;
const stageOrder = ["Applied", "Screened", "Phone Interview", "On-site Interview", "Offer", "Hired"];
const fromIdx = stageOrder.findIndex(s => s.toLowerCase().includes(fromStage.toLowerCase().split(" ")[0]));
const toIdx = stageOrder.findIndex(s => s.toLowerCase().includes(toStage.toLowerCase().split(" ")[0]));
const direction = toIdx > fromIdx ? "forward" : toIdx < fromIdx ? "backward" : "lateral";
const isProgression = direction === "forward" ? "yes" : "no";
return {
  stage_transition: fromStage + " -> " + toStage,
  transition_direction: direction,
  is_progression: isProgression,
  velocity_days: daysInPrev,
  velocity_status: daysInPrev <= 7 ? "fast" : daysInPrev <= 14 ? "normal" : "slow",
  dashboard_row: vars.job_title + " | " + vars.candidate_name + " | " + fromStage + " -> " + toStage + " | " + daysInPrev + "d"
};`,
          outputVariables: [
            { name: "stage_transition", type: "string" },
            { name: "transition_direction", type: "string" },
            { name: "is_progression", type: "string" },
            { name: "velocity_days", type: "number" },
            { name: "velocity_status", type: "string" },
            { name: "dashboard_row", type: "string" },
          ],
        },
      }),

      // 4. Live dashboard update (Sheets)
      sheetsNode(
        "4",
        "Update live pipeline dashboard (Sheets)",
        760,
        "PipelineDashboard",
        '[["{event_timestamp}","{job_title}","{job_id}","{candidate_name}","{from_stage}","{to_stage}","{transition_direction}","{velocity_days}","{velocity_status}","{is_stalled}"]]',
      ),

      // 5. Stalled stage condition
      n("5", "condition", "Is candidate stalled (>14 days in stage)?", 1000, {
        conditionData: [
          {
            variable: "is_stalled",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Recruiter Slack alert (True path)
      slackNode(
        "6",
        "Slack: Stalled candidate alert",
        1240,
        ":warning: Stalled Candidate Alert\\nCandidate: {candidate_name}\\nJob: {job_title}\\nStage: {from_stage} -> {to_stage}\\nTime in previous stage: {days_in_prev_stage} days ({velocity_status})\\nRecruiter: {recruiter_email}\\n\\nPlease review and take action to move this candidate forward or provide a disposition.",
        400,
      ),

      // 7. Historical snapshot logging
      sheetsNode(
        "7",
        "Log historical pipeline snapshot (Sheets)",
        1480,
        "PipelineHistory",
        '[["{event_timestamp}","{candidate_name}","{candidate_email}","{job_title}","{stage_transition}","{transition_direction}","{velocity_days}","{is_stalled}","{recruiter_email}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // stalled -> recruiter alert
      e("5", "7", "output_2"), // not stalled -> skip alert
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 742. RESUME SCREENER (9 nodes)
  // ===================================================================

  "Elite Resume Screener": {
    summary:
      "Advanced resume screening with bias mitigation. An application trigger fires on new submissions, AI parses the resume, job requirements are loaded, weighted scoring via runCode evaluates candidates, bias mitigation via a second runCode removes identifying information from scoring, a routing condition separates strong from weak candidates, recruiters get Slack notifications for shortlisted candidates, the ATS is updated in Greenhouse, and screening analytics are logged to Sheets.",
    nodes: [
      trigger("Webhook: New job application", "webhook_app", {
        source: "Greenhouse / Lever / Application Form",
        event: "application.submitted",
        howItStarts:
          "A candidate submits a job application through your ATS or application form",
        setup:
          "In Greenhouse -> Webhooks -> 'Application submitted' or Lever -> Webhooks -> 'New Application' -> paste your workflow URL. The AI reads whatever data your ATS sends.",
        inputFields: [],
      }),

      // 2. AI resume parsing
      aiNode(
        "2",
        "AI: Parse resume and application",
        280,
        "You are a resume parsing specialist. Read the incoming application webhook data and extract structured information. Find the candidate name, email, phone, resume text, years of experience, education, skills list, job title applied for, and job requirements regardless of the webhook format. Respond JSON: {candidate_name: string, candidate_email: string, candidate_phone: string, resume_text: string, years_experience: number, education: string, skills_list: string, job_title: string, job_requirements: string, recruiter_email: string, application_id: string}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "candidate_phone", as: "candidate_phone", type: "string" },
          { field: "resume_text", as: "resume_text", type: "string" },
          { field: "years_experience", as: "years_experience", type: "number" },
          { field: "education", as: "education", type: "string" },
          { field: "skills_list", as: "skills_list", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "job_requirements", as: "job_requirements", type: "string" },
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
          { field: "application_id", as: "application_id", type: "string" },
        ],
      ),

      // 3. Job requirements load (AI)
      aiNode(
        "3",
        "AI: Structure job requirements for scoring",
        520,
        "You are a job requirements analyst. Parse the job requirements into a structured scoring rubric. For each requirement, assign a weight (1-10) based on importance. Categorize as must-have vs nice-to-have. Respond JSON: {must_have_skills: string, nice_to_have_skills: string, min_experience_years: number, required_education: string, scoring_weights_json: string, total_weight_points: number}",
        "Job title: {job_title}. Requirements: {job_requirements}.",
        [
          { field: "must_have_skills", as: "must_have_skills", type: "string" },
          {
            field: "nice_to_have_skills",
            as: "nice_to_have_skills",
            type: "string",
          },
          {
            field: "min_experience_years",
            as: "min_experience_years",
            type: "number",
          },
          {
            field: "required_education",
            as: "required_education",
            type: "string",
          },
          {
            field: "scoring_weights_json",
            as: "scoring_weights_json",
            type: "string",
          },
          {
            field: "total_weight_points",
            as: "total_weight_points",
            type: "number",
          },
        ],
      ),

      // 4. Weighted scoring (runCode)
      n("4", "runCode", "Calculate weighted candidate score", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score candidate against weighted requirements
const weights = JSON.parse(vars.scoring_weights_json || "{}");
const skills = (vars.skills_list || "").toLowerCase();
const resume = (vars.resume_text || "").toLowerCase();
const mustHave = (vars.must_have_skills || "").split(",").map(s => s.trim().toLowerCase());
const niceToHave = (vars.nice_to_have_skills || "").split(",").map(s => s.trim().toLowerCase());
let mustHaveScore = 0;
let mustHaveMax = mustHave.length * 10;
const mustHaveMatches = [];
for (const skill of mustHave) {
  if (skill && (skills.includes(skill) || resume.includes(skill))) {
    mustHaveScore += 10;
    mustHaveMatches.push(skill);
  }
}
let niceScore = 0;
let niceMax = niceToHave.length * 5;
for (const skill of niceToHave) {
  if (skill && (skills.includes(skill) || resume.includes(skill))) niceScore += 5;
}
const expYears = parseInt(vars.years_experience) || 0;
const minExp = parseInt(vars.min_experience_years) || 0;
const expScore = expYears >= minExp ? 20 : Math.max(0, 20 - (minExp - expYears) * 5);
const totalMax = mustHaveMax + niceMax + 20;
const rawScore = mustHaveScore + niceScore + expScore;
const normalizedScore = totalMax > 0 ? Math.round((rawScore / totalMax) * 100) : 0;
const passThreshold = 60;
return {
  raw_score: rawScore,
  normalized_score: normalizedScore,
  must_have_matched: mustHaveMatches.join(", "),
  must_have_match_rate: mustHaveMax > 0 ? Math.round((mustHaveScore / mustHaveMax) * 100) : 0,
  experience_score: expScore,
  passes_threshold: normalizedScore >= passThreshold ? "yes" : "no",
  score_breakdown: "Must-have: " + mustHaveScore + "/" + mustHaveMax + ", Nice-to-have: " + niceScore + "/" + niceMax + ", Experience: " + expScore + "/20"
};`,
          outputVariables: [
            { name: "raw_score", type: "number" },
            { name: "normalized_score", type: "number" },
            { name: "must_have_matched", type: "string" },
            { name: "must_have_match_rate", type: "number" },
            { name: "experience_score", type: "number" },
            { name: "passes_threshold", type: "string" },
            { name: "score_breakdown", type: "string" },
          ],
        },
      }),

      // 5. Bias mitigation (runCode)
      n("5", "runCode", "Apply bias mitigation to scoring", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Bias mitigation: ensure scoring was based on qualifications, not demographics
const score = parseInt(vars.normalized_score) || 0;
const name = vars.candidate_name || "";
const resume = (vars.resume_text || "").toLowerCase();
// Check if any non-qualification factors could have influenced scoring
const biasIndicators = ["photo", "age", "gender", "nationality", "marital"];
const biasFlags = biasIndicators.filter(b => resume.includes(b));
const hasBiasRisk = biasFlags.length > 0;
// Create anonymized identifier for blind review
const anonymizedId = "CAND-" + Math.random().toString(36).substring(2, 8).toUpperCase();
// Adjust score only if bias factors detected (remove potential positive/negative bias)
const adjustedScore = score; // Score is already skills-based, no adjustment needed
const biasReport = hasBiasRisk
  ? "Resume contains potential bias indicators (" + biasFlags.join(", ") + "). Scoring based solely on qualifications."
  : "No bias indicators detected. Scoring based solely on qualifications.";
return {
  adjusted_score: adjustedScore,
  anonymized_id: anonymizedId,
  bias_check_passed: "yes",
  bias_report: biasReport,
  bias_flags_found: biasFlags.join(", ") || "none",
  screening_decision: adjustedScore >= 60 ? "shortlist" : "reject"
};`,
          outputVariables: [
            { name: "adjusted_score", type: "number" },
            { name: "anonymized_id", type: "string" },
            { name: "bias_check_passed", type: "string" },
            { name: "bias_report", type: "string" },
            { name: "bias_flags_found", type: "string" },
            { name: "screening_decision", type: "string" },
          ],
        },
      }),

      // 6. Routing condition
      n("6", "condition", "Candidate shortlisted (score >= 60)?", 1240, {
        conditionData: [
          {
            variable: "screening_decision",
            value: "shortlist",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Recruiter shortlist notification (Slack) — True path
      slackNode(
        "7",
        "Slack: Shortlisted candidate notification",
        1480,
        ":star: Shortlisted Candidate\\nAnonymized ID: {anonymized_id}\\nJob: {job_title}\\nScore: {adjusted_score}/100\\n{score_breakdown}\\nMust-have skills matched: {must_have_matched}\\nBias check: {bias_report}\\n\\nReview in Greenhouse: Application #{application_id}",
      ),

      // 8. ATS update (Greenhouse)
      greenhouseNode(
        "8",
        "Update candidate stage (Greenhouse)",
        1720,
        "POST",
        "applications/{application_id}/advance",
        [],
        [{ name: "id", customName: "greenhouse_advance_id", type: "string" }],
        400,
      ),

      // 9. Screening analytics logging (Sheets)
      sheetsNode(
        "9",
        "Log screening analytics (Sheets)",
        1960,
        "ResumeScreeningAnalytics",
        '[["{anonymized_id}","{job_title}","{adjusted_score}","{must_have_match_rate}","{experience_score}","{screening_decision}","{bias_flags_found}","{bias_check_passed}","{score_breakdown}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // shortlisted -> Slack + Greenhouse
      e("6", "9", "output_2"), // rejected -> log only
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 743. RESUME SCREENER BOT (8 nodes)
  // ===================================================================

  "Resume Screener Bot": {
    summary:
      "Interactive recruiter-facing resume screening bot. A recruiter upload trigger accepts a batch of resumes, batch ingestion structures all resumes, the recruiter selects a role, AI performs deep screening against role requirements, comparative ranking via runCode scores and ranks all candidates, detailed reports are generated, interactive results are delivered via sendMessage, and screening notes are pushed to Greenhouse ATS.",
    nodes: [
      trigger("Webhook: Recruiter resume batch upload", "webhook_app", {
        source: "Internal Portal / Slack",
        event: "resumes.batch.uploaded",
        howItStarts:
          "Recruiter uploads a batch of resumes for screening via the internal portal or Slack bot",
        setup:
          "Configure the internal hiring portal or Slack bot to POST resume batch data to your workflow URL. Payload should include resume content and the target role.",
        inputFields: [],
      }),

      // 2. Batch resume ingestion + structuring
      aiNode(
        "2",
        "AI: Ingest and structure resume batch",
        280,
        "You are a resume parsing specialist. Read the incoming batch upload and extract structured data for each resume. For each candidate, extract: name, email, years of experience, education, skills, and a brief professional summary. Count the total resumes. Respond JSON: {recruiter_email: string, total_resumes: number, candidates_json: string, target_role: string, batch_id: string}",
        "{webhook_payload}",
        [
          { field: "recruiter_email", as: "recruiter_email", type: "string" },
          { field: "total_resumes", as: "total_resumes", type: "number" },
          { field: "candidates_json", as: "candidates_json", type: "string" },
          { field: "target_role", as: "target_role", type: "string" },
          { field: "batch_id", as: "batch_id", type: "string" },
        ],
      ),

      // 3. Role selection (AI)
      aiNode(
        "3",
        "AI: Load role requirements for screening",
        520,
        "You are a job requirements specialist. Given the target role, generate comprehensive screening criteria including must-have skills, nice-to-have skills, minimum experience, education requirements, and key competencies. Create scoring weights for each criterion. Respond JSON: {role_requirements: string, must_have_criteria: string, nice_to_have_criteria: string, min_years: number, scoring_rubric_json: string, cultural_fit_indicators: string}",
        "Target role: {target_role}. Total candidates to screen: {total_resumes}.",
        [
          {
            field: "role_requirements",
            as: "role_requirements",
            type: "string",
          },
          {
            field: "must_have_criteria",
            as: "must_have_criteria",
            type: "string",
          },
          {
            field: "nice_to_have_criteria",
            as: "nice_to_have_criteria",
            type: "string",
          },
          { field: "min_years", as: "min_years", type: "number" },
          {
            field: "scoring_rubric_json",
            as: "scoring_rubric_json",
            type: "string",
          },
          {
            field: "cultural_fit_indicators",
            as: "cultural_fit_indicators",
            type: "string",
          },
        ],
      ),

      // 4. AI deep screening
      aiNode(
        "4",
        "AI: Deep screen all candidates",
        760,
        "You are a senior technical recruiter. Screen each candidate against the role requirements. For each candidate, provide: a fit score (0-100), strengths, weaknesses, red flags, and a recommendation (strong_yes, yes, maybe, no). Be objective and evidence-based. Respond JSON: {screened_candidates_json: string, strong_yes_count: number, yes_count: number, maybe_count: number, no_count: number, screening_notes: string}",
        "Role: {target_role}. Requirements: {role_requirements}. Must-have: {must_have_criteria}. Nice-to-have: {nice_to_have_criteria}. Min years: {min_years}. Candidates: {candidates_json}.",
        [
          {
            field: "screened_candidates_json",
            as: "screened_candidates_json",
            type: "string",
          },
          { field: "strong_yes_count", as: "strong_yes_count", type: "number" },
          { field: "yes_count", as: "yes_count", type: "number" },
          { field: "maybe_count", as: "maybe_count", type: "number" },
          { field: "no_count", as: "no_count", type: "number" },
          { field: "screening_notes", as: "screening_notes", type: "string" },
        ],
      ),

      // 5. Comparative ranking (runCode)
      n("5", "runCode", "Rank candidates comparatively", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Rank screened candidates and generate comparative analysis
const candidates = JSON.parse(vars.screened_candidates_json || "[]");
candidates.sort((a, b) => (b.fit_score || 0) - (a.fit_score || 0));
const ranked = candidates.map((c, i) => ({
  rank: i + 1,
  name: c.name || c.candidate_name || "",
  email: c.email || c.candidate_email || "",
  score: c.fit_score || 0,
  recommendation: c.recommendation || "unknown",
  strengths: c.strengths || "",
  weaknesses: c.weaknesses || ""
}));
const topCandidates = ranked.filter(r => r.recommendation === "strong_yes" || r.recommendation === "yes");
const rankingSummary = ranked.slice(0, 5).map(r =>
  "#" + r.rank + " " + r.name + " (Score: " + r.score + "/100, Rec: " + r.recommendation + ")"
).join("\\n");
return {
  ranked_candidates_json: JSON.stringify(ranked),
  ranking_summary: rankingSummary,
  top_candidate_name: ranked[0] ? ranked[0].name : "No candidates",
  top_candidate_score: ranked[0] ? ranked[0].score : 0,
  shortlist_count: topCandidates.length,
  total_screened: ranked.length
};`,
          outputVariables: [
            { name: "ranked_candidates_json", type: "string" },
            { name: "ranking_summary", type: "string" },
            { name: "top_candidate_name", type: "string" },
            { name: "top_candidate_score", type: "number" },
            { name: "shortlist_count", type: "number" },
            { name: "total_screened", type: "number" },
          ],
        },
      }),

      // 6. Detailed reports generation (AI)
      aiNode(
        "6",
        "AI: Generate detailed screening reports",
        1240,
        "You are a recruitment reporting specialist. Generate a comprehensive screening report suitable for a recruiter. Include an executive summary, detailed candidate profiles with pros/cons, comparative analysis, and recommended next steps for each candidate tier (strong yes, yes, maybe, no). Respond JSON: {executive_summary: string, detailed_report: string, next_steps: string, interview_recommendations: string}",
        "Role: {target_role}. Total screened: {total_screened}. Shortlisted: {shortlist_count}. Rankings: {ranking_summary}. Screening notes: {screening_notes}. Strong yes: {strong_yes_count}, Yes: {yes_count}, Maybe: {maybe_count}, No: {no_count}.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "detailed_report", as: "detailed_report", type: "string" },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "interview_recommendations",
            as: "interview_recommendations",
            type: "string",
          },
        ],
      ),

      // 7. Interactive results (sendMessage)
      n("7", "sendMessage", "Deliver interactive screening results", 1480, {
        sendMessageDetails: {
          message:
            "Resume Screening Complete for: {target_role}\\n\\nScreened: {total_screened} resumes | Shortlisted: {shortlist_count}\\n\\nTop Rankings:\\n{ranking_summary}\\n\\n{executive_summary}\\n\\nNext Steps:\\n{next_steps}\\n\\nInterview Recommendations:\\n{interview_recommendations}\\n\\n[View Full Report] [Schedule Interviews] [Export to ATS]",
        },
      }),

      // 8. ATS screening notes (Greenhouse)
      greenhouseNode(
        "8",
        "Push screening notes to ATS (Greenhouse)",
        1720,
        "POST",
        "candidates/{candidate_id}/activity_feed/notes",
        [
          { key: "user_id", value: "{greenhouse_user_id}" },
          {
            key: "body",
            value:
              "AI Screening Results - Score: {top_candidate_score}/100. {screening_notes}. Recommendation: {executive_summary}",
          },
          { key: "visibility", value: "public" },
        ],
        [{ name: "id", customName: "greenhouse_note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 744. SALARY BENCHMARKING RESEARCH AGENT (9 nodes)
  // ===================================================================

  "Salary Benchmarking Research Agent": {
    summary:
      "AI-powered compensation research and benchmarking. A compensation request trigger starts the flow, AI standardizes the title and level, market data is pulled from compensation APIs, geography adjustment via runCode normalizes for location, AI synthesizes total compensation data, internal equity is compared, a recommendation with rationale is generated, the report is emailed, and the compensation database is updated in Sheets.",
    nodes: [
      trigger("Webhook: Compensation benchmarking request", "webhook_app", {
        source: "Internal Portal / Slack",
        event: "compensation.benchmark.requested",
        howItStarts:
          "HR or a hiring manager requests salary benchmarking data for a specific role, level, and location",
        setup:
          "Configure your internal portal or Slack bot to POST benchmarking requests to your workflow URL. Payload should include role title, level, location, and requestor info.",
        inputFields: [],
      }),

      // 2. Title/level standardization (AI)
      aiNode(
        "2",
        "AI: Standardize title and level",
        280,
        "You are a compensation analyst. Read the incoming benchmarking request and standardize the job title and level to match common compensation frameworks. Map to standard levels (IC1-IC6, M1-M4, D1-D3, VP, SVP, C-level). Extract location, department, and requestor details regardless of payload format. Respond JSON: {requestor_email: string, requestor_name: string, original_title: string, standardized_title: string, level_code: string, level_name: string, location_city: string, location_country: string, department: string, currency: string}",
        "{webhook_payload}",
        [
          { field: "requestor_email", as: "requestor_email", type: "string" },
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "original_title", as: "original_title", type: "string" },
          {
            field: "standardized_title",
            as: "standardized_title",
            type: "string",
          },
          { field: "level_code", as: "level_code", type: "string" },
          { field: "level_name", as: "level_name", type: "string" },
          { field: "location_city", as: "location_city", type: "string" },
          { field: "location_country", as: "location_country", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),

      // 3. Market data pull
      n("3", "hitApi", "Pull market compensation data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.payscale.com/v1/compensation?title={standardized_title}&level={level_code}&country={location_country}&city={location_city}",
          headers: [
            { key: "Authorization", value: "Bearer {payscale_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.base_salary.percentile_25",
              customName: "market_p25_base",
              type: "number",
            },
            {
              name: "data.base_salary.percentile_50",
              customName: "market_p50_base",
              type: "number",
            },
            {
              name: "data.base_salary.percentile_75",
              customName: "market_p75_base",
              type: "number",
            },
            {
              name: "data.total_compensation.percentile_50",
              customName: "market_p50_total",
              type: "number",
            },
            {
              name: "data.sample_size",
              customName: "market_sample_size",
              type: "number",
            },
          ],
        },
      }),

      // 4. Geography adjustment (runCode)
      n("4", "runCode", "Apply geography cost-of-living adjustment", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply geographic cost-of-living adjustments to market data
const city = (vars.location_city || "").toLowerCase();
const country = (vars.location_country || "").toLowerCase();
// COL multipliers relative to US national average (1.0)
const colMultipliers = {
  "san francisco": 1.45, "new york": 1.40, "seattle": 1.30, "boston": 1.35,
  "los angeles": 1.25, "chicago": 1.10, "austin": 1.05, "denver": 1.10,
  "london": 1.20, "berlin": 0.85, "toronto": 1.05, "sydney": 1.15,
  "singapore": 1.10, "tokyo": 1.05, "mumbai": 0.40, "sao paulo": 0.50
};
const multiplier = colMultipliers[city] || (country === "us" || country === "united states" ? 1.0 : 0.85);
const p25 = Math.round(parseFloat(vars.market_p25_base) * multiplier) || 0;
const p50 = Math.round(parseFloat(vars.market_p50_base) * multiplier) || 0;
const p75 = Math.round(parseFloat(vars.market_p75_base) * multiplier) || 0;
const totalP50 = Math.round(parseFloat(vars.market_p50_total) * multiplier) || 0;
const range = p25 + " - " + p75;
return {
  adjusted_p25: p25,
  adjusted_p50: p50,
  adjusted_p75: p75,
  adjusted_total_p50: totalP50,
  col_multiplier: multiplier.toFixed(2),
  adjusted_range: range,
  geo_note: "Adjusted for " + (vars.location_city || "unknown") + " cost of living (multiplier: " + multiplier.toFixed(2) + "x)"
};`,
          outputVariables: [
            { name: "adjusted_p25", type: "number" },
            { name: "adjusted_p50", type: "number" },
            { name: "adjusted_p75", type: "number" },
            { name: "adjusted_total_p50", type: "number" },
            { name: "col_multiplier", type: "string" },
            { name: "adjusted_range", type: "string" },
            { name: "geo_note", type: "string" },
          ],
        },
      }),

      // 5. AI total-comp synthesis
      aiNode(
        "5",
        "AI: Synthesize total compensation analysis",
        1000,
        "You are a total compensation specialist. Given the geo-adjusted market data, synthesize a complete total compensation picture. Include base salary ranges, typical bonus percentages for this level, equity/stock expectations, benefits value, and signing bonus norms. Provide market competitiveness assessment. Respond JSON: {total_comp_analysis: string, recommended_base_range: string, bonus_pct_typical: string, equity_notes: string, benefits_value_estimate: string, market_position: string}",
        "Title: {standardized_title}. Level: {level_name} ({level_code}). Location: {location_city}, {location_country}. Currency: {currency}. Base range: {adjusted_range}. P50 base: {adjusted_p50}. P50 total: {adjusted_total_p50}. Sample size: {market_sample_size}. {geo_note}.",
        [
          {
            field: "total_comp_analysis",
            as: "total_comp_analysis",
            type: "string",
          },
          {
            field: "recommended_base_range",
            as: "recommended_base_range",
            type: "string",
          },
          {
            field: "bonus_pct_typical",
            as: "bonus_pct_typical",
            type: "string",
          },
          { field: "equity_notes", as: "equity_notes", type: "string" },
          {
            field: "benefits_value_estimate",
            as: "benefits_value_estimate",
            type: "string",
          },
          { field: "market_position", as: "market_position", type: "string" },
        ],
      ),

      // 6. Internal equity comparison (AI)
      aiNode(
        "6",
        "AI: Compare internal equity",
        1240,
        "You are a compensation equity analyst. Compare the market benchmarking data against typical internal pay equity considerations. Flag any potential concerns: pay compression with existing employees, equity gaps between similar roles, or market premiums that could cause internal friction. Provide guidance on positioning within the range. Respond JSON: {equity_assessment: string, compression_risk: string, positioning_recommendation: string, equity_action_items: string}",
        "Title: {standardized_title}. Level: {level_code}. Department: {department}. Market P25: {adjusted_p25}. Market P50: {adjusted_p50}. Market P75: {adjusted_p75}. Total comp: {total_comp_analysis}. Recommended base: {recommended_base_range}.",
        [
          {
            field: "equity_assessment",
            as: "equity_assessment",
            type: "string",
          },
          { field: "compression_risk", as: "compression_risk", type: "string" },
          {
            field: "positioning_recommendation",
            as: "positioning_recommendation",
            type: "string",
          },
          {
            field: "equity_action_items",
            as: "equity_action_items",
            type: "string",
          },
        ],
      ),

      // 7. Recommendation with rationale (AI)
      aiNode(
        "7",
        "AI: Generate final recommendation",
        1480,
        "You are a senior compensation consultant. Synthesize all data into a final recommendation with clear rationale. Provide a specific salary offer recommendation, total compensation estimate, and justification that accounts for market data, geography, internal equity, and competitive positioning. Respond JSON: {final_recommendation: string, recommended_salary: string, total_comp_estimate: string, rationale: string, negotiation_ceiling: string, confidence_level: string}",
        "Role: {standardized_title} ({level_name}). Location: {location_city}. Market range: {adjusted_range}. Market position: {market_position}. Total comp: {total_comp_analysis}. Equity: {equity_assessment}. Compression risk: {compression_risk}. Positioning: {positioning_recommendation}.",
        [
          {
            field: "final_recommendation",
            as: "final_recommendation",
            type: "string",
          },
          {
            field: "recommended_salary",
            as: "recommended_salary",
            type: "string",
          },
          {
            field: "total_comp_estimate",
            as: "total_comp_estimate",
            type: "string",
          },
          { field: "rationale", as: "rationale", type: "string" },
          {
            field: "negotiation_ceiling",
            as: "negotiation_ceiling",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 8. Report email
      emailNode(
        "8",
        "Send benchmarking report",
        1720,
        "{requestor_email}",
        "Salary Benchmarking Report: {standardized_title} ({level_name}) - {location_city}",
        "Hi {requestor_name},\\n\\nHere is your compensation benchmarking report.\\n\\nRole: {standardized_title} ({level_name})\\nLocation: {location_city}, {location_country}\\n{geo_note}\\n\\nMarket Data (sample: {market_sample_size}):\\n- P25: {adjusted_p25}\\n- P50 (median): {adjusted_p50}\\n- P75: {adjusted_p75}\\n\\nRecommendation: {recommended_salary}\\nTotal Comp Estimate: {total_comp_estimate}\\nConfidence: {confidence_level}\\n\\nRationale:\\n{rationale}\\n\\nEquity Assessment:\\n{equity_assessment}\\n\\nBonus: {bonus_pct_typical}\\nEquity/Stock: {equity_notes}\\n\\nAction Items:\\n{equity_action_items}\\n\\nNegotiation ceiling: {negotiation_ceiling}",
      ),

      // 9. Compensation database logging (Sheets)
      sheetsNode(
        "9",
        "Log to compensation database (Sheets)",
        1960,
        "CompensationBenchmarks",
        '[["{standardized_title}","{level_code}","{location_city}","{location_country}","{adjusted_p25}","{adjusted_p50}","{adjusted_p75}","{recommended_salary}","{total_comp_estimate}","{col_multiplier}","{confidence_level}","{market_sample_size}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 745. TIME OFF REQUEST BOT (7 nodes)
  // ===================================================================

  "Time Off Request Bot": {
    summary:
      "Conversational bot for submitting and managing time-off requests. A chat trigger captures the request, AI extracts leave type and dates, BambooHR validates balance and policy compliance, a validation failure condition explains issues, the request is submitted for approval via Slack, the employee receives a decision notification, and calendar plus Slack status are updated upon approval.",
    nodes: [
      trigger("Webhook: Time-off request chat message", "webhook_app", {
        source: "Slack / MS Teams / Internal Portal",
        event: "chat.message.timeoff",
        howItStarts:
          "Employee sends a time-off request via Slack command, Teams bot, or internal portal chat",
        setup:
          "Configure Slack slash command (/timeoff) or Teams bot to POST time-off requests to your workflow URL. Payload includes user identity and request details.",
        inputFields: [],
      }),

      // 2. AI leave type/date extraction
      aiNode(
        "2",
        "AI: Extract leave details",
        280,
        "You are an HR time-off assistant. Read the incoming request payload and extract: employee email, employee name, leave type (vacation, sick, personal, bereavement, jury_duty, parental, other), start date, end date, reason/notes, and whether it is a half-day request. Normalize dates to YYYY-MM-DD. Calculate business days. Respond JSON: {employee_email: string, employee_name: string, leave_type: string, start_date: string, end_date: string, business_days: number, reason: string, is_half_day: string, manager_email: string}",
        "{webhook_payload}",
        [
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "leave_type", as: "leave_type", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "business_days", as: "business_days", type: "number" },
          { field: "reason", as: "request_reason", type: "string" },
          { field: "is_half_day", as: "is_half_day", type: "string" },
          { field: "manager_email", as: "manager_email", type: "string" },
        ],
      ),

      // 3. Balance + policy check (BambooHR)
      bambooNode(
        "3",
        "Check balance and policy (BambooHR)",
        520,
        "GET",
        "employees/{employee_id}/time_off/calculator?end={end_date}",
        [],
        [
          {
            name: "categories",
            customName: "balance_categories_json",
            type: "string",
          },
        ],
      ),

      // 4. Validation (runCode embedded in condition via AI pre-check)
      aiNode(
        "4a",
        "AI: Validate request against balance",
        700,
        "You are an HR policy validator. Check if the time-off request is valid based on the employee balance data. Verify: sufficient balance for the leave type, no policy violations (e.g., minimum notice period, blackout dates, consecutive day limits), and no conflicts. Respond JSON: {is_valid: string, validation_message: string, available_balance: number, balance_after: number, policy_warnings: string}",
        "Leave type: {leave_type}. Days: {business_days}. Start: {start_date}. End: {end_date}. Half day: {is_half_day}. Balance data: {balance_categories_json}.",
        [
          { field: "is_valid", as: "is_valid", type: "string" },
          {
            field: "validation_message",
            as: "validation_message",
            type: "string",
          },
          {
            field: "available_balance",
            as: "available_balance",
            type: "number",
          },
          { field: "balance_after", as: "balance_after", type: "number" },
          { field: "policy_warnings", as: "policy_warnings", type: "string" },
        ],
      ),

      // 4. Validation failure condition
      n("4", "condition", "Request passes validation?", 940, {
        conditionData: [
          {
            variable: "is_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval submission (Slack) — True path
      slackNode(
        "5",
        "Slack: Submit for manager approval",
        1180,
        ":calendar: Time Off Approval Request\\nEmployee: {employee_name}\\nType: {leave_type}\\nDates: {start_date} to {end_date} ({business_days} business days)\\nReason: {request_reason}\\nBalance available: {available_balance} | After approval: {balance_after}\\n{policy_warnings}\\n\\n:white_check_mark: Approve | :x: Deny",
      ),

      // 6. Employee decision notification
      emailNode(
        "6",
        "Notify employee of request status",
        1420,
        "{employee_email}",
        "Time Off Request Update: {leave_type} - {start_date} to {end_date}",
        "Hi {employee_name},\\n\\nYour time-off request has been submitted for approval.\\n\\nType: {leave_type}\\nDates: {start_date} to {end_date}\\nBusiness days: {business_days}\\nCurrent balance: {available_balance}\\nBalance after: {balance_after}\\n\\n{validation_message}\\n\\n{policy_warnings}\\n\\nYour manager has been notified and will review your request. You will receive an update once a decision is made.",
      ),

      // 7. Calendar + Slack status update
      n("7", "hitApi", "Update calendar and Slack status", 1660, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "OOO: {employee_name} - {leave_type}" },
            { key: "start", value: '{"date":"{start_date}"}' },
            { key: "end", value: '{"date":"{end_date}"}' },
            { key: "transparency", value: "opaque" },
            {
              key: "description",
              value:
                "Auto-created by Time Off Request Bot. Type: {leave_type}. Days: {business_days}.",
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4a"),
      e("4a", "4"),
      e("4", "5", "output_1"), // valid -> Slack approval
      e("4", "6", "output_2"), // invalid -> notify employee with explanation
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 746. TIMESHEET REMINDER AUTOMATION (8 nodes)
  // ===================================================================

  "Timesheet Reminder Automation": {
    summary:
      "Automated timesheet completion reminders tied to pay periods. A pay-period end trigger starts the scan, incomplete timesheets are identified in BambooHR, urgency is classified via runCode, standard reminder emails go out, an urgent reminder condition escalates overdue cases, managers receive Slack alerts for their delinquent reports, cutoff policy is applied for unresolved cases, and compliance rates are logged to Sheets.",
    nodes: [
      trigger("Schedule: Pay-period end timesheet scan", "schedule", {
        schedule:
          "Every Friday at 2:00 PM and last business day of month at 10:00 AM",
        howItStarts:
          "Scheduled scan runs near pay-period close to identify employees with incomplete timesheets",
        setup:
          "Configure schedule to align with your pay-period end dates (bi-weekly or monthly). Set up BambooHR API credentials with timesheet access.",
        inputFields: [],
      }),

      // 2. Incomplete timesheet scan (BambooHR)
      bambooNode(
        "2",
        "Scan incomplete timesheets (BambooHR)",
        280,
        "GET",
        "time_tracking/timesheets?status=incomplete&start={period_start}&end={period_end}",
        [],
        [
          {
            name: "timesheets",
            customName: "incomplete_timesheets_json",
            type: "string",
          },
        ],
      ),

      // 3. Urgency classification (runCode)
      n("3", "runCode", "Classify timesheet urgency levels", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify urgency of incomplete timesheets based on deadline proximity
const timesheets = JSON.parse(vars.incomplete_timesheets_json || "[]");
const now = new Date();
const cutoffDate = new Date(now);
cutoffDate.setDate(cutoffDate.getDate() + 2); // 2 days until payroll cutoff
const standard = [];
const urgent = [];
for (const ts of timesheets) {
  const empEmail = ts.employeeEmail || ts.employee_email || "";
  const empName = ts.employeeName || ts.employee_name || "";
  const mgrEmail = ts.supervisorEmail || ts.manager_email || "";
  const hoursLogged = ts.hoursLogged || ts.hours_logged || 0;
  const hoursExpected = ts.hoursExpected || ts.hours_expected || 40;
  const completionPct = hoursExpected > 0 ? Math.round((hoursLogged / hoursExpected) * 100) : 0;
  const entry = { email: empEmail, name: empName, manager_email: mgrEmail, hours_logged: hoursLogged, hours_expected: hoursExpected, completion_pct: completionPct };
  if (completionPct < 50) urgent.push(entry);
  else standard.push(entry);
}
const standardEmails = standard.map(s => s.email).filter(Boolean).join(",");
const urgentEmails = urgent.map(u => u.email).filter(Boolean).join(",");
const managerEmails = [...new Set([...urgent].map(u => u.manager_email))].filter(Boolean).join(",");
return {
  total_incomplete: timesheets.length,
  standard_count: standard.length,
  urgent_count: urgent.length,
  standard_emails: standardEmails,
  urgent_emails: urgentEmails,
  manager_alert_emails: managerEmails,
  has_urgent: urgent.length > 0 ? "yes" : "no",
  compliance_rate: timesheets.length > 0 ? Math.round(((timesheets.length - standard.length - urgent.length) / timesheets.length) * 100) : 100,
  urgency_summary: "Total incomplete: " + timesheets.length + " (Standard: " + standard.length + ", Urgent: " + urgent.length + ")"
};`,
          outputVariables: [
            { name: "total_incomplete", type: "number" },
            { name: "standard_count", type: "number" },
            { name: "urgent_count", type: "number" },
            { name: "standard_emails", type: "string" },
            { name: "urgent_emails", type: "string" },
            { name: "manager_alert_emails", type: "string" },
            { name: "has_urgent", type: "string" },
            { name: "compliance_rate", type: "number" },
            { name: "urgency_summary", type: "string" },
          ],
        },
      }),

      // 4. Standard reminder email
      emailNode(
        "4",
        "Send standard timesheet reminders",
        760,
        "{standard_emails}",
        "Reminder: Please Complete Your Timesheet",
        "Hello,\\n\\nFriendly reminder that your timesheet for the current pay period is not yet complete.\\n\\n{urgency_summary}\\n\\nPlease log your remaining hours in BambooHR before the payroll cutoff.\\n\\nTimesheet link: https://app.bamboohr.com/time_tracking\\n\\nIf you have questions about logging hours, please contact your manager or HR.",
      ),

      // 5. Urgent reminder condition
      n("5", "condition", "Are there urgently incomplete timesheets?", 1000, {
        conditionData: [
          {
            variable: "has_urgent",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Manager alert (Slack) — True path
      slackNode(
        "6",
        "Slack: Alert managers of delinquent timesheets",
        1240,
        ":rotating_light: Urgent Timesheet Alert\\n{urgency_summary}\\n\\n{urgent_count} employee(s) have less than 50% timesheet completion with payroll cutoff approaching.\\n\\nManagers: please follow up with your team members to ensure timesheets are completed before the cutoff deadline.\\n\\nAffected employees have been sent individual reminders.",
        400,
      ),

      // 7. Cutoff policy application (email to urgent)
      emailNode(
        "7",
        "Send urgent cutoff warning",
        1480,
        "{urgent_emails}",
        "URGENT: Timesheet Due Before Payroll Cutoff",
        "IMPORTANT: Your timesheet is significantly incomplete and the payroll cutoff is approaching.\\n\\n{urgency_summary}\\n\\nIf your timesheet is not completed by the cutoff deadline, your pay may be processed based on available hours only. Missing hours will be adjusted in the following pay period.\\n\\nComplete your timesheet immediately: https://app.bamboohr.com/time_tracking\\n\\nIf you are unable to complete your timesheet due to system issues, contact HR immediately.",
        400,
      ),

      // 8. Compliance rate logging (Sheets)
      sheetsNode(
        "8",
        "Log compliance rate (Sheets)",
        1720,
        "TimesheetCompliance",
        '[["{total_incomplete}","{standard_count}","{urgent_count}","{compliance_rate}","{urgency_summary}","{manager_alert_emails}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // urgent -> manager Slack + cutoff warning
      e("5", "8", "output_2"), // no urgent -> log compliance
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 747. TRAINING COMPLETION TRACKER (8 nodes)
  // ===================================================================

  "Training Completion Tracker": {
    summary:
      "Tracks training completions from LMS webhooks. An LMS webhook fires on course completion, completion data is extracted via runCode, a certificate generation condition checks eligibility, certificate delivery emails go to the employee, team celebration messages are posted to Slack, the training log is updated in Sheets, managers are notified when all team training is complete, and compliance gaps are closed.",
    nodes: [
      trigger("Webhook: LMS course completion", "webhook_app", {
        source: "LMS (Cornerstone / Udemy Business / Docebo)",
        event: "course.completed",
        howItStarts:
          "Learning Management System fires a webhook when an employee completes a training course or module",
        setup:
          "In your LMS -> Integrations -> Webhooks -> Event: 'Course Completed' -> URL: your workflow URL. Ensure the webhook includes employee identity, course details, and completion data.",
        inputFields: [],
      }),

      // 2. Completion extraction (runCode)
      n("2", "runCode", "Extract completion data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse LMS completion webhook data
const payload = JSON.parse(vars.webhook_payload || "{}");
const user = payload.user || payload.learner || payload.employee || {};
const course = payload.course || payload.training || {};
const completion = payload.completion || payload.result || {};
const empName = user.name || user.displayName || (user.first_name + " " + user.last_name) || "";
const empEmail = user.email || user.workEmail || "";
const managerEmail = user.manager_email || user.supervisorEmail || "";
const courseName = course.name || course.title || "";
const courseId = String(course.id || "");
const courseType = course.type || course.category || "general";
const isCompliance = (courseType.toLowerCase().includes("compliance") || courseType.toLowerCase().includes("mandatory") || course.mandatory === true) ? "yes" : "no";
const score = completion.score || completion.grade || completion.percentage || 0;
const passThreshold = course.pass_threshold || 80;
const passed = score >= passThreshold;
const certEligible = passed && (course.certificate_available !== false);
const completedAt = completion.completed_at || completion.timestamp || new Date().toISOString();
const duration = completion.duration_minutes || course.duration_minutes || 0;
const department = user.department || "";
return {
  employee_name: empName,
  employee_email: empEmail,
  manager_email: managerEmail,
  course_name: courseName,
  course_id: courseId,
  course_type: courseType,
  is_compliance: isCompliance,
  score: score,
  passed: passed ? "yes" : "no",
  certificate_eligible: certEligible ? "yes" : "no",
  completed_at: completedAt,
  duration_minutes: duration,
  department: department
};`,
          outputVariables: [
            { name: "employee_name", type: "string" },
            { name: "employee_email", type: "string" },
            { name: "manager_email", type: "string" },
            { name: "course_name", type: "string" },
            { name: "course_id", type: "string" },
            { name: "course_type", type: "string" },
            { name: "is_compliance", type: "string" },
            { name: "score", type: "number" },
            { name: "passed", type: "string" },
            { name: "certificate_eligible", type: "string" },
            { name: "completed_at", type: "string" },
            { name: "duration_minutes", type: "number" },
            { name: "department", type: "string" },
          ],
        },
      }),

      // 3. Certificate generation condition
      n(
        "3",
        "condition",
        "Is certificate eligible (passed + cert available)?",
        520,
        {
          conditionData: [
            {
              variable: "certificate_eligible",
              value: "yes",
              comparison_operator: "EQUAL TO",
              logical_operator: "AND",
            },
          ],
        },
      ),

      // 4. Certificate delivery email (True path)
      emailNode(
        "4",
        "Send completion certificate email",
        760,
        "{employee_email}",
        "Congratulations! Certificate of Completion: {course_name}",
        "Hi {employee_name},\\n\\nCongratulations on completing {course_name}!\\n\\nYour Results:\\n- Score: {score}%\\n- Status: Passed\\n- Duration: {duration_minutes} minutes\\n- Completed: {completed_at}\\n\\nYour certificate of completion is attached to this email and has been added to your training record.\\n\\nKeep up the great work in your professional development!",
      ),

      // 5. Team celebration (Slack)
      slackNode(
        "5",
        "Slack: Team celebration message",
        1000,
        ":tada: Training Completion!\\n{employee_name} has completed *{course_name}*!\\nScore: {score}% | Type: {course_type}\\nDuration: {duration_minutes} min\\n\\nGreat job! :clap:",
      ),

      // 6. Training log update (Sheets)
      sheetsNode(
        "6",
        "Update training log (Sheets)",
        1240,
        "TrainingCompletions",
        '[["{employee_name}","{employee_email}","{department}","{course_name}","{course_id}","{course_type}","{is_compliance}","{score}","{passed}","{certificate_eligible}","{completed_at}","{duration_minutes}"]]',
      ),

      // 7. Manager full-completion notification (AI determines if team is complete)
      aiNode(
        "7",
        "AI: Check team training completion status",
        1480,
        "You are a training compliance analyst. Given the employee completion data, check if this completion closes any training requirements for the team. Determine if the manager should be notified of a milestone (e.g., entire team now compliant, all mandatory training complete). Generate a manager notification if warranted. Respond JSON: {manager_notify: string, manager_message: string, team_completion_pct: number, compliance_gap_closed: string, remaining_gaps: string}",
        "Employee: {employee_name} ({department}). Course: {course_name} (type: {course_type}, compliance: {is_compliance}). Score: {score}%. Passed: {passed}. Manager: {manager_email}.",
        [
          { field: "manager_notify", as: "manager_notify", type: "string" },
          { field: "manager_message", as: "manager_message", type: "string" },
          {
            field: "team_completion_pct",
            as: "team_completion_pct",
            type: "number",
          },
          {
            field: "compliance_gap_closed",
            as: "compliance_gap_closed",
            type: "string",
          },
          { field: "remaining_gaps", as: "remaining_gaps", type: "string" },
        ],
      ),

      // 8. Compliance gap closure notification
      emailNode(
        "8",
        "Notify manager of team training status",
        1720,
        "{manager_email}",
        "Training Update: {employee_name} Completed {course_name} - Team at {team_completion_pct}%",
        "Hello,\\n\\nTraining completion update for your team.\\n\\n{manager_message}\\n\\nEmployee: {employee_name}\\nCourse: {course_name} ({course_type})\\nScore: {score}%\\nTeam completion: {team_completion_pct}%\\n\\nCompliance gap closed: {compliance_gap_closed}\\nRemaining gaps: {remaining_gaps}\\n\\nTraining dashboard: https://lms.example.com/manager/dashboard",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // cert eligible -> send certificate
      e("3", "5", "output_2"), // no cert -> skip to celebration
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
